import React, { useRef } from 'react';
import Heading from '../../../components/typography';
import { LogoUploadContainer, LogoErrorMessage } from './whiteLabel.styled';
import Spinner from '../../../components/spinner';

interface FaviconUploadProps {
  faviconPreview: {
    content: string | null;
    name: string | null;
  };
  onFaviconChange: (file: File) => void;
  onDeleteClick: () => void;
  errors?: any;
  touched?: any;
  isLoading?: boolean;
  disabled?: boolean;
  id?: string;
}

const FaviconUpload = ({
  faviconPreview,
  onFaviconChange,
  onDeleteClick,
  errors,
  touched,
  isLoading = false,
  disabled = false,
  id,
}: FaviconUploadProps): JSX.Element => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      onFaviconChange(file);
    }
    // Reset input value to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const hasError = Boolean(errors?.favicon) && Boolean(touched?.favicon);

  // Check if content is a URL, data URL, or base64
  const getImageSrc = (): string | null => {
    if (!faviconPreview?.content) return null;
    
    const content = faviconPreview.content;
    // Already a data URL
    if (content.startsWith('data:')) return content;
    // HTTP/HTTPS URL
    if (content.startsWith('http://') || content.startsWith('https://')) return content;
    // Relative URL (starts with /)
    if (content.startsWith('/')) return content;
    // Assume it's base64
    return `data:image/png;base64,${content}`;
  };
  
  const imageSrc = getImageSrc();

  return (
    <div id={id}>
      <LogoUploadContainer id="faviconUploadContainer" className={`favicon-upload ${hasError ? 'has-error' : ''}`}>
        <div className={`logo-preview favicon-preview ${hasError ? 'is-invalid' : ''}`} id="faviconPreview">
          {isLoading ? (
            <div className='loading-container' id="faviconLoadingContainer">
              <Spinner />
            </div>
          ) : (
            <>
              {!disabled && (
                <label className='upload-label icon-only favicon-upload-btn' role='button' id="faviconUploadLabel">
                  <span className='material-symbols-outlined icon' id="faviconUploadIcon">add_a_photo</span>
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='.ico, .png, .jpg, .jpeg'
                    onChange={handleFileChange}
                    id="faviconFileInput"
                  />
                </label>
              )}
              {!disabled && faviconPreview?.content && (
                <label
                  className='delete-icon'
                  role='button'
                  onClick={onDeleteClick}
                  id="faviconDeleteLabel"
                >
                  <span className='material-symbols-outlined icon' id="faviconDeleteIcon">delete</span>
                </label>
              )}
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={faviconPreview.name ?? 'Favicon'}
                  id="faviconImage"
                />
              ) : (
                <div className='default-logo' id="defaultFaviconContainer">
                  <span className='material-symbols-outlined placeholder-icon' id="faviconPlaceholderIcon">star</span>
                  <Heading variant='body-xs' title='No favicon' className='placeholder-text' id="faviconPlaceholderText" />
                </div>
              )}
            </>
          )}
        </div>
      </LogoUploadContainer>

      {hasError && (
        <LogoErrorMessage id="faviconErrorMessage">
          {errors.favicon}
        </LogoErrorMessage>
      )}
    </div>
  );
};

export default FaviconUpload;
